import { useState } from 'react'

import {
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Stack,
  HStack,
  Box,
  FormLabel,
  FormControl,
  Input,
  Badge,
  ModalCloseButton,
  ModalContent,
} from '@chakra-ui/react'
import { Select, SelectItem } from 'src/components/Select/Select'
import { WarningMessage } from 'src/components/WarningMessage/WarningMessage'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { useRejectAgreement } from '../../api'
import {
  ExplanationDocRevision,
  ExplanationDocRevisionAgreementForm,
} from '../../types'

type Props = {
  docRevisions: ExplanationDocRevisionAgreementForm[]
  isOpen: boolean
  onClose: () => void
  mutateExplanation: () => void
}

export const RejectAgreementModalWithSelect: React.FC<Props> = ({
  docRevisions,
  isOpen,
  onClose,
  mutateExplanation,
}) => {
  const [selectedUid, setSelectedUid] =
    useState<ExplanationDocRevision['uid']>()
  const [reason, setReason] = useState('')

  const toast = useMirohaToast()

  const { request: rejectAgreement } = useRejectAgreement({
    onSuccess: () => {
      toast({
        status: 'success',
        title: '同意を拒否しました',
      })
      onClose()

      // 非同期でstatusが更新されるので直後のmutateではstatusが更新されない場合があるので2秒遅延させてmutateを行う
      setTimeout(() => {
        mutateExplanation()
      }, 2000)
    },
    onError: error => {
      toast({
        status: 'error',
        title: error.message,
      })
    },
  })

  const selectItems: SelectItem<
    ExplanationDocRevision['uid'],
    React.ReactNode
  >[] = docRevisions.map(docRevision => ({
    value: docRevision.uid,
    label: (
      <HStack spacing="2">
        <Text as="span">{`${docRevision.icfDocumentRevisionName}`}</Text>
        {docRevision.icfDocumentRevisionIsRequiredAgreement && (
          <Badge bg="blue.400" color="white">
            同意必須
          </Badge>
        )}
      </HStack>
    ),
  }))

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>同意拒否</ModalHeader>
        <ModalBody>
          <Stack spacing="8">
            <Stack spacing="2">
              <Text>同意文書について患者の同意拒否を記録します</Text>
              <WarningMessage message="この操作は取り消せません。" />
            </Stack>
            <Stack spacing="4">
              <Box>
                <FormLabel>同意書</FormLabel>
                <Select
                  value={selectedUid}
                  items={selectItems}
                  onChange={setSelectedUid}
                />
              </Box>
              <FormControl>
                <FormLabel>コメント</FormLabel>
                <Input
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                />
              </FormControl>
            </Stack>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <ModalCancelButton />
          <Button
            colorScheme="red"
            isDisabled={!reason || !selectedUid}
            onClick={async () => {
              if (!reason || !selectedUid) return
              await rejectAgreement({
                explanationDocRevisionUid: selectedUid,
                reason,
              })
            }}
          >
            同意拒否する
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
